// AnalyticsDashboard.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, NavLink, Outlet, useLocation } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './AnalyticsDashboard.css';
import { authAPI, surveyAPI, analyticsAPI, chartAPI } from '../services/apiClient';
import { useBusiness } from '../services/BusinessContext';

// Import core components
import QuestionAnalyticsWithSettings from './AnalyticsComponents/QuestionAnalyticsWithSettings';

// Note: Advanced analytics components removed as they use non-existent APIs:
// - MergeAnalyticsDashboard (analyticsAPI.getMergedAnalytics, surveyAPI.getLinks)
// - ResponseTimeAnalyticsPanel (surveyAPI.getResponseTimesAdvanced)
// - DropoutAnalysisPanel (surveyAPI.getDropoutAnalysis)
// - GridAnalytics (analyticsAPI.getFilteredQuestionAnalytics, getQuestionAnalyticsUnified)
// - WordCloudViewer (surveyAPI.getOpenEndedWithUsers)
// - ReportTabPage (reportTabAPI)
// - BatchReportCustomization (reportTabAPI, various non-existent endpoints)

// question types excluded from standard analysis/PDF but included in Excel export
const EXCLUDED_ANALYSIS_TYPES = new Set(['document-upload', 'signature', 'date-picker', 'email-input', 'content-text', 'content-media']);
const PDF_OPEN_ENDED_LIMIT = 5;

export default function AnalyticsDashboard() {
  const { surveyId, linkCode, businessId: businessIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { business, isBusinessAdmin, isSuperAdmin } = useBusiness();
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [isRawExporting, setIsRawExporting] = useState(false);

  // Chart settings state
  const [globalChartType, setGlobalChartType] = useState('bar');
  const [globalChartColor, setGlobalChartColor] = useState('#36A2EB');
  const [globalShowPercentages, setGlobalShowPercentages] = useState(true);
  const [globalShowLegend, setGlobalShowLegend] = useState(true);
  const [questionSettings, setQuestionSettings] = useState({});
  const [questionOptions, setQuestionOptions] = useState({});

  // Question order state for drag and drop
  const [questionOrder, setQuestionOrder] = useState([]);

  const businessIdFromState = location.state?.businessId;
  const analyticsBasePath = React.useMemo(() => {
    if (!surveyId) {
      return '/analytics';
    }

    const idSegment = businessIdParam || businessIdFromState;
    if (idSegment) {
      return `/analytics/${surveyId}/${idSegment}`;
    }

    return `/analytics/${surveyId}`;
  }, [surveyId, businessIdParam, businessIdFromState]);

  const normalizePath = (path) => (path || '').replace(/\/+$/, '');

  // Remove selectedQuestionId since we're showing all analyses
  const [selectedQuestion, setSelectedQuestion] = useState(null);

  // Function to update question-specific settings
  const updateQuestionSetting = async (questionId, field, value) => {
    try {
      let updatedSettings;

      // Handle both formats: field+value or entire settings object
      if (field === null && typeof value === 'object') {
        // New format: entire settings object is passed
        updatedSettings = {
          ...questionSettings,
          [questionId]: value
        };
      } else {
        // Old format: field and value are passed separately
        updatedSettings = {
          ...questionSettings,
          [questionId]: {
            ...(questionSettings[questionId] || {}),
            [field]: value
          }
        };
      }

      setQuestionSettings(updatedSettings);

      // Save to backend using chartAPI
      await chartAPI.saveChartSettings(surveyId, {
        global: {
          chartType: globalChartType,
          chartColor: globalChartColor,
          showPercentages: globalShowPercentages,
          showLegend: globalShowLegend
        },
        questions: updatedSettings
      });

      return true; // Return success for async handling
    } catch (err) {
      console.error('Error saving chart settings:', err);
      // Optionally revert the state if save fails
      // You could show a toast notification here
      return false; // Return failure for async handling
    }
  };

  // Handle drag and drop reordering
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(orderedAnalyzableQuestions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Update question order state
    const newOrder = items.map((q, index) => ({ id: q.id, order: index + 1 }));
    setQuestionOrder(newOrder);

    // Update each question's displayOrder setting
    newOrder.forEach(({ id, order }) => {
      updateQuestionSetting(id, 'displayOrder', order);
    });
  };

  // Helper function to clone elements and convert charts for PDF
  const createCloneWithCharts = (sourceEl, sectionType = 'all') => {

    const clone = sourceEl.cloneNode(true);
    const normalizeText = (value) => (value || '').replace(/\s+/g, ' ').trim().toLowerCase();

    const sourceTitleText = sourceEl
      .querySelector('.question-analytics-header .header-content')
      ?.textContent
      ?.replace(/\s+/g, ' ')
      ?.trim();
    const sourceAnalyticsTitle = sourceEl
      .querySelector('.analytics-panel-title')
      ?.textContent
      ?.replace(/\s+/g, ' ')
      ?.trim();
    const normalizedSourceTitle = normalizeText(
      (sourceAnalyticsTitle || sourceTitleText || '').replace(/^#?\d+\s*:?\s*/, '')
    );

    // Remove interactive UI 
    clone
      .querySelectorAll(
        '.drag-handle, .settings-column, .settings-toggle, .order-control, .pdf-include-control, .settings-buttons-container, .action-buttons, .settings-section, .header-row'
      )
      .forEach((el) => el.remove());
    clone.querySelectorAll('.question-analytics-header').forEach((el) => el.remove());
    clone.querySelectorAll('.chart-placeholder').forEach((el) => el.remove());
    clone.querySelectorAll('.analytics-panel-title').forEach((el) => el.remove());
    clone.querySelectorAll('.analytics-panel-container > .flex').forEach((el) => el.remove());
    clone.querySelectorAll('.analytics-panel-container .text-sm').forEach((el) => el.remove());

    // Remove in-chart duplicated question labels while keeping the PDF header title card.
    if (normalizedSourceTitle) {
      clone.querySelectorAll('h1, h2, h3, h4, h5, .chart-title, .question-title').forEach((el) => {
        const normalizedCandidate = normalizeText((el.textContent || '').replace(/^#?\d+\s*:?\s*/, ''));
        if (normalizedCandidate && normalizedCandidate === normalizedSourceTitle) {
          el.remove();
        }
      });
    }

    // Remove layout class that reserves space for settings pane
    clone.querySelectorAll('.question-analytics-grid').forEach((el) => {
      el.classList.remove('with-settings');
      el.style.gridTemplateColumns = '1fr';
      el.style.minHeight = '0';
      el.style.height = 'auto';
      el.style.gap = '8px';
    });

    // --- FIX 1: Grid Layout Shifts & Gaps in PDF ---

    // Fix: Force static positioning. 
    // Sticky positioning (often used for table headers) causes html2canvas to miscalculate offsets, 
    // leading to the "shifted right" text overlapping issue.
    clone.querySelectorAll('.grid-table th, .grid-table td, .row-label, .grid-table-container').forEach(el => {
      el.style.position = 'static';
      el.style.transform = 'none';
      el.style.left = 'auto';
      el.style.right = 'auto';
      el.style.top = 'auto';
      el.style.bottom = 'auto';
    });

    // Fix: Remove large gaps.
    // We aggressively reset margins on the Grid Analysis container and its headers.
    clone.querySelectorAll('.grid-analysis').forEach((el) => {
      el.style.marginTop = '5px';
      el.style.marginBottom = '5px';
      el.style.padding = '0';
      el.style.height = 'auto';
    });

    clone.querySelectorAll('.grid-analysis h4').forEach((el) => {
      el.style.margin = '0 0 8px 0'; // Tight gap between title and table
      if (!el.style.background && !el.style.backgroundColor) {
        el.style.padding = '0';
      }
      el.style.fontSize = '16px';
      el.style.borderBottom = 'none'; // Optional: remove border if it adds visual noise
    });

    // Ensure table container doesn't have overflow issues
    clone.querySelectorAll('.grid-table-container').forEach((el) => {
      el.style.marginTop = '0';
      el.style.paddingTop = '0';
      el.style.overflow = 'visible';
      el.style.maxHeight = 'none';
    });

    clone.querySelectorAll('.ranking-table-container').forEach((el) => {
      el.style.margin = '6px 0';
      el.style.padding = '8px';
      el.style.boxShadow = 'none';
    });

    clone.querySelectorAll('.ranking-matrix-wrapper').forEach((el) => {
      el.style.margin = '8px 0';
      el.style.boxShadow = 'none';
    });

    clone.querySelectorAll('.chart-column').forEach((el) => {
      el.style.padding = '0';
      el.style.borderRight = 'none';
      el.style.overflow = 'visible';
    });

    // Section filtering
    try {
      if (sectionType === 'charts') {
        // Remove all table/stat blocks so titles don't separate from tables in PDF
        clone.querySelectorAll('.statistics-panel, .grid-analysis').forEach((el) => el.remove());
        clone.querySelectorAll('table').forEach((el) => el.remove());
        clone.querySelectorAll('.demographics-table, .ranking-distribution-table').forEach((el) => el.remove());
      } else if (sectionType === 'tables') {
        // Remove visuals
        clone.querySelectorAll('.chart-container, .chart-wrapper, .chart-placeholder, .word-cloud-analysis, .custom-chart-legend').forEach((el) => el.remove());

        clone.querySelectorAll('canvas').forEach((el) => el.remove());
        clone.querySelectorAll('svg').forEach((el) => el.remove());
      }

    } catch (e) {
      console.warn('[PDF Export] Section filtering error:', e);
    }

    // Replace canvases with images (charts) — but make them fluid to fill the card width
    const srcCanvases = sourceEl.querySelectorAll('canvas');
    const cloneCanvases = clone.querySelectorAll('canvas');

    if (sectionType !== 'tables') {
      srcCanvases.forEach((canvas, idx) => {
        try {
          const img = document.createElement('img');
          img.src = canvas.toDataURL('image/png');

          // CRUCIAL: make charts scale to container width in PDF
          img.style.display = 'block';
          img.style.width = '100%';      // fill the card width
          img.style.maxWidth = '100%';
          img.style.height = 'auto';
          img.setAttribute('data-pdf-chart', '1');

          if (cloneCanvases[idx]) cloneCanvases[idx].replaceWith(img);
        } catch (e) {
          console.warn(`[PDF Export] Could not convert canvas ${idx + 1} to image:`, e);
        }
      });

      // Also ensure wrappers don't constrain width
      clone.querySelectorAll('.chart-container, .chart-wrapper').forEach(w => {
        w.style.width = '100%';
        w.style.maxWidth = 'none';
        w.style.overflow = 'visible';
      });
    }

    // Basic styling for white background and sane body width for capture
    clone.style.background = 'white';
    clone.style.padding = '16px';
    clone.style.paddingBottom = '24px';
    clone.style.boxSizing = 'border-box';
    clone.style.overflow = 'visible';

    // Prevent footer/legend clipping in exported chart cards.
    clone.querySelectorAll('.question-chart, .chart-container, .chart-wrapper, .custom-chart-legend, .slider-legend-note').forEach((el) => {
      el.style.overflow = 'visible';
    });
    clone.querySelectorAll('.question-chart').forEach((el) => {
      el.style.paddingBottom = '18px';
    });
    clone.querySelectorAll('.custom-chart-legend').forEach((el) => {
      el.style.marginBottom = '14px';
    });
    clone.querySelectorAll('.slider-legend-note').forEach((el) => {
      el.style.paddingBottom = '10px';
      el.style.whiteSpace = 'normal';
    });

    // Normalize text/line-height for readability
    clone.querySelectorAll('p, h1, h2, h3, h4, h5, h6, div, span, td, th, li').forEach(textEl => {
      if (!textEl.style.color) {
        textEl.style.color = '#000';
      }
      textEl.style.lineHeight = '1.5';
    });

    // Restore star colors overridden by the global text color
    clone.querySelectorAll('.star-shell .star-empty').forEach((el) => {
      el.style.color = '#e0e0e0';
    });
    clone.querySelectorAll('.star-shell .star-fill').forEach((el) => {
      el.style.color = '#AA2EFF';
    });

    // Improve star rendering in PDF (avoid clipping/grey overlay)
    clone.querySelectorAll('.star-rating-visual').forEach((wrapper) => {
      wrapper.style.display = 'inline-flex';
      wrapper.style.alignItems = 'center';
      wrapper.style.gap = '3px';
      wrapper.style.whiteSpace = 'nowrap';
    });

    clone.querySelectorAll('.star-shell').forEach((shell) => {
      const empty = shell.querySelector('.star-empty');
      const fill = shell.querySelector('.star-fill');
      const size = empty ? parseFloat(window.getComputedStyle(empty).fontSize) : null;
      const boxSize = Number.isFinite(size) ? Math.round(size * 1.3) : null;

      if (boxSize) {
        shell.style.width = `${size}px`;
        shell.style.height = `${boxSize}px`;
        shell.style.lineHeight = `${boxSize}px`;
      }
      shell.style.position = 'relative';
      shell.style.overflow = 'visible';
      shell.style.display = 'inline-block';
      shell.style.verticalAlign = 'middle';

      [empty, fill].forEach((star) => {
        if (!star) return;
        star.style.position = 'absolute';
        star.style.inset = '0';
        star.style.display = 'block';
        if (boxSize) {
          star.style.fontSize = `${size}px`;
          star.style.lineHeight = `${boxSize}px`;
        }
        star.style.textAlign = 'center';
      });

      if (fill) {
        fill.style.overflow = 'hidden';
        fill.style.color = '#AA2EFF';
      }
      if (empty) {
        empty.style.color = '#e0e0e0';
      }
    });

    // Allow legend labels to wrap in PDF to avoid truncation
    clone.querySelectorAll('.custom-chart-legend__label').forEach((el) => {
      el.style.whiteSpace = 'normal';
    });

    // Limit open-ended responses in PDF export
    clone.querySelectorAll('[data-open-ended-block]').forEach((block) => {
      const list = block.querySelector('[data-open-ended-responses]');
      if (list) {
        const items = Array.from(list.querySelectorAll('[data-open-ended-response]'));
        items.forEach((item, idx) => {
          if (idx >= PDF_OPEN_ENDED_LIMIT) {
            item.remove();
          } else {
            item.style.display = '';
          }
        });
      }

      block.querySelectorAll('.open-ended-pagination, .see-more-row, .open-ended-see-more').forEach((el) => el.remove());

      const countEl = block.querySelector('.open-ended-response-count');
      if (countEl) {
        const total = Number(countEl.getAttribute('data-total') || 0);
        const totalDisplay = total || (list ? list.querySelectorAll('[data-open-ended-response]').length : 0);
        const shown = Math.min(PDF_OPEN_ENDED_LIMIT, totalDisplay);
        countEl.textContent = `Showing ${shown} of ${totalDisplay} responses (PDF view limited)`;
      }
    });

    return clone;
  };

  // Helper to wrap a section in a PDF-only card container
  const createPdfCard = (contentEl, titleText = '') => {
    const card = document.createElement('div');
    card.style.border = '1px solid #e5e7eb';
    card.style.borderRadius = '8px';
    card.style.padding = '16px';
    card.style.margin = '12px 0';
    card.style.background = '#ffffff';

    if (titleText) {
      const header = document.createElement('div');
      header.style.fontSize = '16px';
      header.style.fontWeight = '600';
      header.style.marginBottom = '10px';
      header.style.color = '#000000';
      header.textContent = titleText;
      card.appendChild(header);
    }

    // Ensure content fits within card width
    contentEl.style.maxWidth = '100%';
    card.appendChild(contentEl);
    return card;
  };

  // Force a new page after each question (grouped: charts + tables)
  // - Uses cropping (no negative offsets) so rows aren't duplicated
  // - Packs slices within a page, but never mixes two different questions on one page
  const exportToPDF = async () => {
    // Respect your existing visibility settings
    const questionsToInclude = orderedAnalyzableQuestions.filter(
      q => questionSettings[q.id]?.isHidden !== true
    );
    if (!questionsToInclude.length) {
      alert('No content is marked for PDF export.');
      return;
    }

    setIsExporting(true);

    try {
      // 1) Build groups: [title cover], then one group per question (charts + tables)
      const blocks = [];

      // (Optional) put title on its own cover page
      const titleOnCover = true;

      // Title block
      const titleContainer = document.createElement('div');
      titleContainer.style.background = '#fff';
      titleContainer.style.padding = '36px 24px';
      titleContainer.style.textAlign = 'center';
      titleContainer.style.width = '100%';
      const h1 = document.createElement('h1');
      h1.textContent = `Analytics Report: ${survey?.title || 'Survey'}`;
      h1.style.margin = '0 0 8px 0';
      h1.style.fontSize = '26px';
      h1.style.fontWeight = '800';
      h1.style.color = '#000';
      titleContainer.appendChild(h1);
      const p = document.createElement('p');
      p.textContent = `Generated on: ${new Date().toLocaleDateString()}`;
      p.style.margin = '0';
      p.style.fontSize = '12px';
      p.style.color = '#333';
      titleContainer.appendChild(p);

      blocks.push({
        node: titleContainer,
        pageBreakAfter: titleOnCover,   // cover → next content starts on a new page
        allowSplit: false,
      });

      // One group per question (split into PDF blocks for clean page breaks)
      questionsToInclude.forEach((q, qIndex) => {
        const questionEl = document.querySelector(`[data-question-id="${q.id}"]`);
        if (!questionEl) return;

        const questionCards = [];
        const pushCard = (node, allowSplit = false) => {
          questionCards.push({ node, allowSplit });
        };

        // Question header card (title + total responses)
        const headerCard = document.createElement('div');
        headerCard.style.border = '1px solid #e5e7eb';
        headerCard.style.borderRadius = '6px';
        headerCard.style.padding = '10px';
        headerCard.style.margin = '6px 0';
        headerCard.style.background = '#fff';

        const headerTitle = document.createElement('div');
        headerTitle.className = 'pdf-question-title';
        const domTitle = questionEl.querySelector('.analytics-panel-title')?.textContent?.trim();
        const numberValue = q.sequence_number ?? (qIndex + 1);
        const questionLabel = numberValue ? `#${numberValue}: ` : '';
        headerTitle.textContent = domTitle
          ? `${questionLabel}${domTitle}`
          : `${questionLabel}${q.question_text || 'Question'}`;
        headerCard.appendChild(headerTitle);

        const domTotal = Array.from(questionEl.querySelectorAll('p'))
          .map((el) => el.textContent || '')
          .find((text) => text.toLowerCase().includes('total responses'));
        if (domTotal) {
          const totalEl = document.createElement('div');
          totalEl.className = 'pdf-question-total';
          totalEl.textContent = domTotal.trim();
          headerCard.appendChild(totalEl);
        }

        pushCard(headerCard, false);

        // Charts (as one compact card)
        const chartsOnly = createCloneWithCharts(questionEl, 'charts');
        const chartsHasContent = chartsOnly && (
          chartsOnly.querySelector('.chart-container') ||
          chartsOnly.querySelector('.chart-wrapper') ||
          chartsOnly.querySelector('.custom-chart-legend') ||
          chartsOnly.querySelector('.word-cloud-analysis') ||
          chartsOnly.querySelector('canvas') ||
          chartsOnly.querySelector('img[data-pdf-chart]')
        );
        if (chartsHasContent) {
          const chartsCard = document.createElement('div');
          chartsCard.style.border = '1px solid #e5e7eb';
          chartsCard.style.borderRadius = '6px';
          chartsCard.style.padding = '10px';
          chartsCard.style.margin = '6px 0';
          chartsCard.style.background = '#fff';
          chartsCard.appendChild(chartsOnly);
          pushCard(chartsCard, false);
        }

        // Tables (keep titles with tables to avoid PDF mis-ordering)
        const tablesOnly = createCloneWithCharts(questionEl, 'tables');
        const rawSections = Array.from(
          tablesOnly.querySelectorAll('.statistics-panel, .grid-analysis')
        );
        const tableSections = rawSections.filter(
          (section) => !section.parentElement?.closest('.statistics-panel')
        );

        const addTableCard = (node) => {
          const card = document.createElement('div');
          card.style.border = '1px solid #e5e7eb';
          card.style.borderRadius = '6px';
          card.style.padding = '10px';
          card.style.margin = '6px 0';
          card.style.background = '#fff';
          card.appendChild(node);
          pushCard(card, true);
        };

        if (tableSections.length) {
          tableSections.forEach((section) => {
            addTableCard(section.cloneNode(true));
          });
        } else {
          const tableNodes = Array.from(tablesOnly.querySelectorAll('table'));
          tableNodes.forEach((tbl) => {
            addTableCard(tbl.cloneNode(true));
          });
        }

        questionCards.forEach((card, idx) => {
          blocks.push({
            node: card.node,
            pageBreakAfter: idx === questionCards.length - 1, // force new page after last card of question
            allowSplit: card.allowSplit,
          });
        });
      });

      // 2) Offscreen root for clean captures
      const scratch = document.createElement('div');
      scratch.className = 'pdf-export-root';
      scratch.style.position = 'fixed';
      scratch.style.left = '-99999px';
      scratch.style.top = '0';
      scratch.style.width = '1100px';       // render wide for crisp PDF; scaled later
      scratch.style.background = '#fff';
      document.body.appendChild(scratch);

      // 3) PDF setup
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
      const margin = { top: 10, right: 10, bottom: 10, left: 10 };
      const fullW = pdf.internal.pageSize.getWidth();
      const fullH = pdf.internal.pageSize.getHeight();
      const pageW = fullW - margin.left - margin.right;
      const pageH = fullH - margin.top - margin.bottom;

      let yCursor = margin.top; // current y (mm) on the active page

      // Helper: gather safe row breaks (px) for current node
      const collectRowBreaks = (rootEl, top) => {
        const s = new Set([0]);
        const addBreak = (node) => {
          const r = node.getBoundingClientRect();
          const b = Math.max(0, Math.floor(r.bottom - top));
          if (b > 0) s.add(b);
        };
        rootEl.querySelectorAll('tr').forEach(addBreak);
        rootEl.querySelectorAll('[data-open-ended-response]').forEach(addBreak);
        const h = Math.floor(rootEl.getBoundingClientRect().height);
        if (h > 0) s.add(h);
        return Array.from(s).sort((a, b) => a - b);
      };

      const boostPdfFontSizes = (rootEl) => {
        rootEl.querySelectorAll('p, h1, h2, h3, h4, h5, h6, div, span, td, th, li, label').forEach((el) => {
          const size = parseFloat(window.getComputedStyle(el).fontSize);
          if (Number.isFinite(size)) {
            el.style.fontSize = `${size + 2}px`;
          }
        });

        rootEl.querySelectorAll('.pdf-question-title').forEach((el) => {
          const size = parseFloat(window.getComputedStyle(el).fontSize);
          if (Number.isFinite(size)) {
            el.style.fontSize = `${size + 4}px`;
          }
          el.style.fontWeight = '700';
          el.style.marginBottom = '6px';
        });

        rootEl.querySelectorAll('.pdf-question-total').forEach((el) => {
          el.style.marginBottom = '4px';
        });

        rootEl.querySelectorAll('.open-ended-responses-list').forEach((el) => {
          el.style.display = 'grid';
          el.style.rowGap = '6px';
        });

        rootEl.querySelectorAll('.open-ended-response-card').forEach((el) => {
          const size = parseFloat(window.getComputedStyle(el).fontSize);
          if (Number.isFinite(size)) {
            el.style.fontSize = `${Math.max(size - 8, 7)}px`;
            el.style.lineHeight = '1.25';
          }
          el.style.padding = '6px 8px';
        });

        rootEl.querySelectorAll('.open-ended-response-count').forEach((el) => {
          const size = parseFloat(window.getComputedStyle(el).fontSize);
          if (Number.isFinite(size)) {
            el.style.fontSize = `${Math.max(size - 4, 9)}px`;
          }
        });
      };

      // 4) Render each block; after each *question* block force a new page
      for (let i = 0; i < blocks.length; i++) {
        const { node: el, pageBreakAfter, allowSplit } = blocks[i];

        el.style.position = 'static';
        el.style.width = '100%';
        el.style.boxSizing = 'border-box';
        el.style.overflow = 'visible';

        scratch.appendChild(el);
        await new Promise(r => setTimeout(r, 20));
        boostPdfFontSizes(el);

        const rect = el.getBoundingClientRect();
        const rowBreaks = collectRowBreaks(el, rect.top);

        const logicalWidth = el.scrollWidth || Math.ceil(rect.width);
        const logicalHeight = el.scrollHeight || Math.ceil(rect.height);

        let scale = 2.4;
        if (logicalHeight > 9000) scale = 2.0;
        if (logicalHeight > 15000) scale = 1.6;

        const canvas = await html2canvas(el, {
          scale,
          useCORS: true,
          backgroundColor: '#ffffff',
          width: logicalWidth,
          height: logicalHeight,
          scrollX: 0,
          scrollY: 0,
          logging: false,
        });

        const ratio = pageW / canvas.width;              // mm/px
        const pageHeightPx = Math.floor(pageH / ratio);  // px we can fit on current page
        const safety = 0;                                 // px near bottom

        let curPx = 0;
        const totalPx = canvas.height;

        const blockHeightMm = totalPx * ratio;
        if (!allowSplit && blockHeightMm <= pageH && yCursor + blockHeightMm > margin.top + pageH) {
          pdf.addPage();
          yCursor = margin.top;
        }

        while (curPx < totalPx) {
          const roomMm = pageH - (yCursor - margin.top);
          let roomPx = Math.floor(roomMm / ratio);
          if (roomPx <= 0) {
            pdf.addPage();
            yCursor = margin.top;
            roomPx = Math.floor(pageH / ratio);
          }

          const idealEnd = Math.min(curPx + roomPx, totalPx);
          let chosenEnd = idealEnd;
          const candidates = rowBreaks.filter(b => b > curPx + 2 && b <= idealEnd - safety);
          if (candidates.length) {
            chosenEnd = candidates[candidates.length - 1];
          } else {
            // If the next row fits on a fresh page, move to a new page instead of splitting the row.
            const nextRowBreak = rowBreaks.find(b => b > curPx + 2);
            if (nextRowBreak && (nextRowBreak - curPx) <= pageHeightPx && yCursor > margin.top + 0.5) {
              pdf.addPage();
              yCursor = margin.top;
              continue;
            }
          }

          const slicePx = Math.max(1, chosenEnd - curPx);

          const sub = document.createElement('canvas');
          sub.width = canvas.width;
          sub.height = slicePx;
          const ctx = sub.getContext('2d');
          ctx.drawImage(canvas, 0, curPx, canvas.width, slicePx, 0, 0, sub.width, sub.height);

          const img = sub.toDataURL('image/png', 1.0);
          const sliceMmH = slicePx * ratio;

          pdf.addImage(img, 'PNG', margin.left, yCursor, pageW, sliceMmH, undefined, 'FAST');
          yCursor += sliceMmH;

          // If we filled the page, open a fresh one automatically
          if (yCursor > fullH - margin.bottom - 0.5) {
            pdf.addPage();
            yCursor = margin.top;
          }

          curPx += slicePx;
        }

        scratch.removeChild(el);

        // *** HERE: force a page break after the whole question group (and after the cover if enabled) ***
        if (pageBreakAfter && i < blocks.length - 1) {
          // Only add a page if we're not already at the top of a fresh one
          if (yCursor !== margin.top) {
            pdf.addPage();
            yCursor = margin.top;
          }
        }
      }

      document.body.removeChild(scratch);
      pdf.save(`${survey?.title || 'Survey'}_Analytics_Report.pdf`);
    } catch (err) {
      console.error('Error generating PDF:', err);
      alert('Error generating PDF. See console for details.');
    } finally {
      setIsExporting(false);
    }
  };

  // Export raw data to Excel
  const exportToExcel = async () => {
    if (!surveyId || isRawExporting) return;
    setIsRawExporting(true);
    try {
      console.log('[EXPORT] Starting Excel export for survey:', surveyId);
      let response = null;

      // Preferred flow: async job to avoid request timeouts on large surveys
      try {
        const startRes = await analyticsAPI.startExcelExportJob(surveyId);
        const jobId = startRes?.data?.job_id;
        if (!jobId) {
          throw new Error('Export job did not return a job ID.');
        }

        const pollIntervalMs = 2000;
        const maxWaitMs = 20 * 60 * 1000; // 20 minutes
        const startedAt = Date.now();

        while (Date.now() - startedAt < maxWaitMs) {
          await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
          const statusRes = await analyticsAPI.getExcelExportJobStatus(surveyId, jobId);
          const status = statusRes?.data?.status;

          if (status === 'ready') {
            response = await analyticsAPI.downloadExcelExportJob(surveyId, jobId);
            break;
          }
          if (status === 'failed') {
            throw new Error(statusRes?.data?.error || 'Export job failed.');
          }
        }

        if (!response) {
          throw new Error('Export is taking too long. Please try again in a moment.');
        }
      } catch (jobErr) {
        // Backward compatibility fallback for environments without async export endpoints
        const status = jobErr?.response?.status;
        if (status === 404 || status === 405) {
          response = await analyticsAPI.exportExcel(surveyId);
        } else {
          throw jobErr;
        }
      }

      // Check if response is an error (JSON instead of blob)
      if (response.data && typeof response.data === 'string' && response.data.includes('error')) {
        console.error('[EXPORT] Server returned error:', response.data);
        alert('Error exporting Excel: Server error. Please try again.');
        return;
      }

      const blob = new Blob([response.data], { type: response.headers?.['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `survey_${surveyId}_raw_data.xlsx`;
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      console.log('[EXPORT] Excel export completed successfully');
    } catch (err) {
      console.error('[EXPORT] Error exporting Excel:', err);
      console.error('[EXPORT] Error details:', {
        message: err.message,
        response: err.response,
        request: err.request,
        config: err.config
      });
      alert(`Error exporting Excel: ${err.message || 'Unknown error'}. Please try again.`);
    } finally {
      setIsRawExporting(false);
    }
  };

  const buildQuestionOptionsFromSurvey = React.useCallback((surveyData) => {
    const optionMap = {};
    const questions = surveyData?.questions || [];

    questions.forEach((question) => {
      const qId = question?.id;
      if (!qId) return;

      if (Array.isArray(question.options) && question.options.length > 0) {
        optionMap[qId] = question.options.map((opt) => ({
          label: typeof opt === 'string' ? opt : (opt?.text || 'Unknown'),
          count: 0
        }));
        return;
      }

      if (Array.isArray(question.scale_points) && question.scale_points.length > 0) {
        optionMap[qId] = question.scale_points.map((point) => ({
          label: String(point),
          count: 0
        }));
        return;
      }

      if (Array.isArray(question.image_options) && question.image_options.length > 0) {
        optionMap[qId] = question.image_options.map((opt) => ({
          label: opt?.label || opt?.hidden_label || 'Unknown',
          count: 0
        }));
      }
    });

    return optionMap;
  }, []);

  const [isSidebarOpen, setIsSidebarOpen] = useState(window.innerWidth > 1024);

  // Filter questions into analyzable and reference-only lists - using useMemo to stabilize dependencies
  const analyzableQuestions = React.useMemo(() => {
    return survey?.questions?.filter(q => !EXCLUDED_ANALYSIS_TYPES.has(q.question_type)) || [];
  }, [survey?.questions]);

  const referenceQuestions = React.useMemo(() => {
    return survey?.questions?.filter(q => EXCLUDED_ANALYSIS_TYPES.has(q.question_type)) || [];
  }, [survey?.questions]);

  // Create ordered analyzable questions based on displayOrder or original sequence
  const orderedAnalyzableQuestions = React.useMemo(() => {
    return [...analyzableQuestions].sort((a, b) => {
      const aOrder = questionSettings[a.id]?.displayOrder || a.sequence_number || 999;
      const bOrder = questionSettings[b.id]?.displayOrder || b.sequence_number || 999;
      return aOrder - bOrder;
    });
  }, [analyzableQuestions, questionSettings]);

  // Check if user is authenticated and handle business context redirection
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      alert('Authentication required. Redirecting...');
      navigate('/login');
      return;
    }

    const businessIdFromParams = businessIdParam;
    const effectiveBusinessId = business?.id || businessIdFromState || businessIdFromParams || survey?.business_id;
    const userRole = localStorage.getItem('userRole');
    const derivedIsBusinessAdmin = isBusinessAdmin || userRole === 'business_admin';

    if (surveyId && effectiveBusinessId) {
      const analyticsContext = {
        surveyId: String(surveyId),
        businessId: String(effectiveBusinessId),
        accessedAt: new Date().toISOString(),
        currentPath: location.pathname,
        userRole: userRole || null,
        isBusinessAdmin: derivedIsBusinessAdmin,
        source: 'analytics-dashboard'
      };
      localStorage.setItem('analytics_business_context', JSON.stringify(analyticsContext));
    }

    // Handle user redirection based on role
    const storedUserRole = userRole;
    if (storedUserRole === 'business_admin' && !effectiveBusinessId) {
      console.warn('[ANALYTICS_DASHBOARD] Business admin reached analytics without business context.');
    }
  }, [navigate, surveyId, business, businessIdParam, businessIdFromState, survey?.business_id, location.pathname, isBusinessAdmin]);

  // Initialize question order state when questions are loaded
  useEffect(() => {
    if (orderedAnalyzableQuestions.length > 0 && questionOrder.length === 0) {
      const initialOrder = orderedAnalyzableQuestions.map((q, index) => ({
        id: q.id,
        order: questionSettings[q.id]?.displayOrder || index + 1
      }));
      setQuestionOrder(initialOrder);
    }
  }, [orderedAnalyzableQuestions, questionOrder.length, questionSettings]);

  // Fetch the main survey details
  useEffect(() => {
    const fetchSurveyData = async () => {
      if (!surveyId) {
        console.error('[ANALYTICS_DASHBOARD] No survey ID provided');
        setError('No survey ID provided');
        setLoading(false);
        return;
      }

      console.log(`[ANALYTICS_DASHBOARD] Starting fetch for survey ${surveyId}`);
      setLoading(true);
      setError(null);

      try {
        console.log(`[ANALYTICS_DASHBOARD] Making API call to get survey ${surveyId}`);
        const response = await surveyAPI.getById(surveyId);

        console.log('[ANALYTICS_DASHBOARD] ✅ Survey fetch successful:', response.data);
        setSurvey(response.data);

        // Load chart settings from backend (backend is the source of truth)
        try {
          const chartSettingsResponse = await chartAPI.getChartSettings(surveyId);
          console.log('[ANALYTICS_DASHBOARD] Raw response from chartAPI:', chartSettingsResponse.data);

          // Backend returns {settings: {...}} structure
          const settings = chartSettingsResponse.data.settings || {};
          setGlobalChartType(settings.global?.chartType || 'bar');
          setGlobalChartColor(settings.global?.chartColor || '#36A2EB');
          setGlobalShowPercentages(settings.global?.showPercentages ?? true);
          setGlobalShowLegend(settings.global?.showLegend ?? true);
          setQuestionSettings(settings.questions || {});

          console.log('[ANALYTICS_DASHBOARD] ✅ Loaded settings from backend:', settings);
        } catch (settingsErr) {
          console.warn('[ANALYTICS_DASHBOARD] Could not load chart settings, using defaults:', settingsErr);
          // Use default settings if loading fails
          setGlobalChartType('bar');
          setGlobalChartColor('#36A2EB');
          setGlobalShowPercentages(true);
          setGlobalShowLegend(true);
          setQuestionSettings({});
        }


        // Build option labels from survey schema to avoid an additional expensive analytics request.
        setQuestionOptions(buildQuestionOptionsFromSurvey(response.data));
      } catch (err) {
        console.error('[ANALYTICS_DASHBOARD] ❌ Error fetching survey:', err);
        console.error('[ANALYTICS_DASHBOARD] Error details:', {
          message: err.message,
          status: err.response?.status,
          statusText: err.response?.statusText,
          data: err.response?.data
        });

        let errorMessage = 'Failed to load survey data';
        if (err.response?.status === 401) {
          errorMessage = 'Authentication required. Please log in again.';
        } else if (err.response?.status === 403) {
          errorMessage = 'Access denied. You do not have permission to view this survey.';
        } else if (err.response?.status === 404) {
          errorMessage = 'Survey not found or has been deleted.';
        } else if (err.response?.data?.error) {
          errorMessage = err.response.data.error;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (surveyId) {
      fetchSurveyData();
    }
  }, [surveyId, buildQuestionOptionsFromSurvey]);

  // Add useEffect for window resize handling
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Close sidebar when clicking overlay (mobile only)
  const handleOverlayClick = () => {
    if (window.innerWidth <= 1024) {
      setIsSidebarOpen(false);
    }
  };

  const handleBackToSurveys = () => {
    // Super admin takes precedence - navigate to saved surveys (all surveys view)
    if (isSuperAdmin) {
      navigate('/savedsurveys');
      return;
    }

    const savedContext = restoreBusinessContext();
    const userRoleFromStorage = localStorage.getItem('userRole');
    const savedUserRole = savedContext?.userRole;
    const savedIsBusinessAdmin = savedContext?.isBusinessAdmin;
    const locationIsBusinessAdmin = location.state?.isBusinessAdmin;

    const derivedIsBusinessAdmin = Boolean(
      isBusinessAdmin ||
      locationIsBusinessAdmin === true ||
      userRoleFromStorage === 'business_admin' ||
      savedUserRole === 'business_admin' ||
      savedIsBusinessAdmin === true
    );

    const effectiveBusinessId = business?.id || businessIdParam || businessIdFromState || savedContext?.businessId || survey?.business_id;

    if (derivedIsBusinessAdmin && effectiveBusinessId) {
      navigate(`/admin/business/${effectiveBusinessId}/surveys/manage`);
      return;
    }

    console.warn('[ANALYTICS_BACK] Unexpected user role or missing business context', {
      isSuperAdmin,
      isBusinessAdmin,
      derivedIsBusinessAdmin,
      businessIdFromContext: business?.id,
      businessIdFromParams: businessIdParam,
      businessIdFromState,
      businessIdFromSaved: savedContext?.businessId,
      surveyBusinessId: survey?.business_id,
      effectiveBusinessId,
      userRoleFromStorage,
      savedUserRole,
      savedIsBusinessAdmin,
      locationIsBusinessAdmin
    });

    navigate('/savedsurveys');
  };

  // Helper function to check if we're on the report view (index route)
  const isReportView = () => {
    return normalizePath(location.pathname) === normalizePath(analyticsBasePath);
  };

  // Helper function to restore business context
  const restoreBusinessContext = () => {
    try {
      const savedContext = localStorage.getItem('analytics_business_context');
      if (savedContext) {
        const context = JSON.parse(savedContext);
        console.log('[ANALYTICS_CONTEXT] Restored business context:', context);
        return context;
      }
    } catch (error) {
      console.error('[ANALYTICS_CONTEXT] Error restoring business context:', error);
    }
    return null;
  };

  if (loading) {
    return (
      <div className="analytics-dashboard-container" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading analytics dashboard...</p>
          <small>Survey ID: {surveyId}</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-dashboard-container" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="error-container">
          <h2>❌ Unable to Load Analytics</h2>
          <p>{error}</p>
          <div className="error-details">
            <strong>Survey ID:</strong> {surveyId}
            <br />
            <small>Check the browser console for more details</small>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!survey) {
    console.warn('[ANALYTICS_DASHBOARD] Survey data is null after successful fetch');
    return (
      <div className="analytics-dashboard-container" style={{ justifyContent: 'center', alignItems: 'center' }}>
        <div className="error-container">
          <h2>⚠️ No Survey Data</h2>
          <p>Survey data could not be loaded.</p>
          <button
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  console.log('[ANALYTICS_DASHBOARD] ✅ Rendering MergeAnalyticsDashboard with survey:', survey.title);

  return (
    <div className="analytics-dashboard-container">
      {/* Overlay */}
      <div
        className={`sidebar-overlay ${isSidebarOpen ? 'open' : ''}`}
        onClick={handleOverlayClick}
      />

      {/* Sidebar */}
      <div className={`analytics-dashboard-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div>
          <h2 className="analytics-dashboard-title">Analytics</h2>
          <nav>
            {/* Back to Survey Button */}
            <button
              className="analytics-dashboard-back"
              onClick={handleBackToSurveys}
            >
              <i className="ri-arrow-left-line"></i>
              Back to Surveys
            </button>

            {/* Report Group */}
            <div className="analytics-sidebar-group">
              <h3>Report</h3>
              <NavLink
                to={analyticsBasePath}
                end
                className={({ isActive }) => `analytics-dashboard-link ${isActive ? "active" : ""}`}
              >
                <i className="ri-file-text-line"></i>
                Report
              </NavLink>
            </div>

            {/* AI Insights Group - Hidden for now */}
          </nav>
        </div>
      </div>

      {/* Fixed Header */}
      <div className="analytics-fixed-header">
        <h2 className="survey-title">{survey?.title}</h2>

        <div className="export-buttons-group">
          <button
            className="export-btn"
            onClick={exportToPDF}
            disabled={isExporting}
            style={isExporting ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
          >
            {isExporting ? (
              <>
                <i className="ri-loader-4-line ri-spin"></i>
                Generating...
              </>
            ) : (
              <>
                <i className="ri-file-pdf-line"></i>
                Export PDF
              </>
            )}
          </button>

          <button
            className="export-btn"
            onClick={exportToExcel}
            disabled={isRawExporting}
            style={isRawExporting ? { opacity: 0.7, cursor: 'not-allowed' } : {}}
          >
            {isRawExporting ? (
              <>
                <i className="ri-loader-4-line ri-spin"></i>
                Generating...
              </>
            ) : (
              <>
                <i className="ri-file-excel-2-line"></i>
                Export Raw Data
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="analytics-dashboard-yo">
        {/* This will render the child routes */}
        <Outlet context={{ surveyId }} />

        {/* Question List Section - Only show on report view */}
        {isReportView() && (
          <>
            <div className="analytics-dashboard-questions">
              <h3>Question Results</h3>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="questions">
                  {(provided) => (
                    <div
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {orderedAnalyzableQuestions.map((q, index) => (
                        <Draggable key={q.id} draggableId={q.id.toString()} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              data-question-id={q.id}
                              style={{
                                ...provided.draggableProps.style,
                                marginBottom: '20px',
                                opacity: snapshot.isDragging ? 0.8 : 1,
                                transform: snapshot.isDragging
                                  ? `${provided.draggableProps.style?.transform} rotate(2deg)`
                                  : provided.draggableProps.style?.transform
                              }}
                            >
                              <div {...provided.dragHandleProps}>
                                <QuestionAnalyticsWithSettings
                                  key={q.id}
                                  question={q}
                                  surveyId={surveyId}
                                  questionSettings={questionSettings[q.id] || {}}
                                  globalSettings={{
                                    chartType: globalChartType,
                                    chartColor: globalChartColor,
                                    showPercentages: globalShowPercentages,
                                    showLegend: globalShowLegend
                                  }}
                                  onSettingsChange={(field, value) => updateQuestionSetting(q.id, field, value)}
                                  questionOptions={questionOptions[q.id] || []}
                                  index={index}
                                />
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>

            {/* Reference Data Section */}
            {referenceQuestions.length > 0 && (
              <div className="analytics-dashboard-reference-questions">
                <h3 style={{ color: 'black', marginTop: '15px' }}>Reference Data (Included in Excel Export)</h3>
                <p className="reference-info" style={{ color: '#333' }}>
                  The following question types are not visualized here but their data is available in the raw data export (Excel):
                  Upload File (Filename), Signature (Yes/No), Date Picker (Date), Email Input (Email).
                </p>
                {referenceQuestions.map((q) => (
                  <div key={q.id} className="analytics-dashboard-question-item reference-item">
                    <div className="question2-text">
                      <strong>#{q.sequence_number || 'N/A'}:</strong> {q.question_text}
                    </div>
                    <span className="reference-note">See Excel Export</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    maxWidth: '1200px',
    margin: '0 auto'
  },
  loading: {
    margin: 20,
    fontSize: '1.1em',
    textAlign: 'center',
    padding: '40px'
  },
  error: {
    color: 'red',
    margin: 20,
    padding: '20px',
    border: '1px solid #ffcccc',
    borderRadius: '4px',
    backgroundColor: '#fff8f8'
  },
  title: {
    fontSize: '1.8em',
    marginBottom: 10,
    color: '#333'
  },
  advancedControls: {
    marginBottom: 20,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px'
  },
  advancedButton: {
    backgroundColor: '#17a2b8',
    color: '#fff',
    border: 'none',
    padding: '8px 14px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  },
  panelContainer: {
    border: '1px solid #ccc',
    padding: 20,
    borderRadius: 4,
    marginBottom: 20,
    backgroundColor: '#fff'
  },
  divider: {
    margin: '20px 0',
    border: 'none',
    borderTop: '1px solid #ddd'
  },
  questionList: {
    marginTop: 15
  },
  questionItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #ddd',
    borderRadius: 4,
    padding: '12px 15px',
    marginBottom: '10px',
    backgroundColor: '#f9f9f9',
    transition: 'background-color 0.2s'
  },
  analyzeButton: {
    backgroundColor: '#28a745',
    border: 'none',
    padding: '8px 14px',
    color: '#fff',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '14px',
    transition: 'background-color 0.2s'
  },
  noQuestions: {
    color: '#666',
    fontStyle: 'italic',
    textAlign: 'center',
    padding: '20px'
  },
  analyticsPanel: {
    marginTop: 20,
    border: '1px solid #aaa',
    padding: 20,
    borderRadius: 4,
    backgroundColor: '#fff'
  },
  analyticsPanelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '15px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px'
  },
  closeButton: {
    backgroundColor: '#dc3545',
    color: '#fff',
    border: 'none',
    padding: '8px 15px',
    borderRadius: 4,
    cursor: 'pointer',
    fontSize: '14px'
  }
};
